import React, { useCallback, useEffect, useState } from 'react';
import { Film, Calendar as CalendarIcon, Clock, AlertCircle, UserPlus } from 'lucide-react';
import { api } from '../../../lib/api';
import AssignShooterDialog from '../../../components/admin/AssignShooterDialog';
import { EventOverrides, PackageOverrides } from '../../../components/admin/MediaAdminOverrides';
import {
  packageStatusBadgeClass,
  packageStatusLabel,
  eventStatusBadgeClass,
  eventStatusLabel,
  noticeTierBadgeClass,
  noticeTierLabel,
  formatMediaDateRange,
  formatExpiryDate,
} from '../../../lib/media';

// MediaTab — slotted under "Records & insights" on AccountDetail.
//
// Renders two stacked sections:
//   1. Packages — current first (active / suspended), then historical
//   2. Recent events — last 10 bookings against any of this account's packages
//
// Read-only in slice 1 (MP.1). Booking creation, restart, etc. land in
// later slices. The "Sell a package" CTA points at the invoice flow once
// MP.7 wires the bundled-proposal handoff.
export default function MediaTab({ accountId, accountName }) {
  const [data, setData] = useState({ packages: [], recentEvents: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assignTarget, setAssignTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/api/admin/accounts/${accountId}/media-packages`);
      setData({
        packages: res?.packages || [],
        recentEvents: res?.recentEvents || [],
      });
    } catch (err) {
      setError(err?.message || 'Failed to load media packages');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="text-[12px] text-gray-500">Loading…</div>;
  }
  if (error) {
    return (
      <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
        {error}
      </div>
    );
  }

  const { packages, recentEvents } = data;

  return (
    <div className="space-y-6">
      <PackagesSection packages={packages} accountName={accountName} onChanged={load} />
      <RecentEventsSection
        events={recentEvents}
        onAssign={(ev) => setAssignTarget(ev)}
        onChanged={load}
      />
      {assignTarget && (
        <AssignShooterDialog
          event={assignTarget}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => { setAssignTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function PackagesSection({ packages, accountName, onChanged }) {
  if (!packages.length) {
    return (
      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#B0FC38]/15 border border-[#B0FC38]/30 flex items-center justify-center shrink-0">
            <Film className="w-4 h-4 text-[#B0FC38]" />
          </div>
          <div>
            <div className="text-sm text-white font-medium">No media packages yet</div>
            <div className="text-[12px] text-gray-500 mt-0.5">
              {accountName ? `${accountName} hasn't purchased a media package.` : 'No packages on this account.'}
              {' '}A package is created automatically when a signed invoice includes a media-package line item.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[13px] font-medium text-white tracking-tight">
          Media packages ({packages.length})
        </h3>
      </div>
      <div className="space-y-2">
        {packages.map((p) => <PackageRow key={p.id} pkg={p} onChanged={onChanged} />)}
      </div>
    </div>
  );
}

function PackageRow({ pkg, onChanged }) {
  const expiringSoon = pkg.status === 'active' && daysUntil(pkg.expiresOn) <= 30;
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-white font-medium">{pkg.label}</span>
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${packageStatusBadgeClass(pkg.status)}`}>
              {packageStatusLabel(pkg.status)}
            </span>
            {expiringSoon && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/[0.1] border border-amber-400/30 text-amber-300">
                Expires {formatExpiryDate(pkg.expiresOn)}
              </span>
            )}
          </div>
          <div className="text-[11px] text-gray-500 mt-1">
            Purchased {formatExpiryDate(pkg.purchaseDate)} · Expires {formatExpiryDate(pkg.expiresOn)}
          </div>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <CreditMeter
            total={pkg.eventsTotal}
            used={pkg.eventsUsed}
            reserved={pkg.eventsReserved}
            remaining={pkg.eventsRemaining}
          />
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-white/[0.05]">
        <PackageOverrides pkg={pkg} onChanged={onChanged} />
      </div>
    </div>
  );
}

function CreditMeter({ total, used, reserved, remaining }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">Events</div>
      <div className="text-[13px] text-white">
        <span className="font-semibold text-[#B0FC38]">{remaining}</span>
        <span className="text-gray-500"> remaining</span>
      </div>
      <div className="text-[10px] text-gray-500 mt-0.5">
        {used} used · {reserved} reserved · {total} total
      </div>
    </div>
  );
}

function RecentEventsSection({ events, onAssign, onChanged }) {
  if (!events.length) {
    return (
      <div>
        <h3 className="text-[13px] font-medium text-white tracking-tight mb-3">Recent events</h3>
        <div className="text-[12px] text-gray-500">No bookings yet for this account.</div>
      </div>
    );
  }
  return (
    <div>
      <h3 className="text-[13px] font-medium text-white tracking-tight mb-3">
        Recent events ({events.length})
      </h3>
      <div className="space-y-1.5">
        {events.map((e) => <EventRow key={e.id} ev={e} onAssign={onAssign} onChanged={onChanged} />)}
      </div>
    </div>
  );
}

function EventRow({ ev, onAssign, onChanged }) {
  const needsAssignment = ev.status === 'pending'
    && !ev.assignedUserId && !ev.externalFallbackName;
  return (
    <div className="rounded-lg border border-white/[0.05] bg-white/[0.02] px-3 py-2.5 flex items-start gap-3 flex-wrap">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] text-white truncate">{ev.eventTitle}</span>
          <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${eventStatusBadgeClass(ev.status)}`}>
            {eventStatusLabel(ev.status)}
          </span>
          {ev.noticeTier !== 'standard' && (
            <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full ${noticeTierBadgeClass(ev.noticeTier)}`}>
              {noticeTierLabel(ev.noticeTier)}
            </span>
          )}
        </div>
        <div className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <CalendarIcon className="w-3 h-3" />
            {formatMediaDateRange(ev.scheduledStartAt, ev.scheduledEndAt)}
          </span>
          {ev.assignedUserName && (
            <span>· {ev.assignedUserName}</span>
          )}
          {!ev.assignedUserName && ev.externalFallbackName && (
            <span>· {ev.externalFallbackName} (external)</span>
          )}
          {!ev.assignedUserName && !ev.externalFallbackName && (
            <span className="inline-flex items-center gap-1 text-amber-300">
              <AlertCircle className="w-3 h-3" /> Unassigned
            </span>
          )}
        </div>
      </div>
      {ev.confirmationDeadlineAt && ev.status === 'pending' && ev.assignedUserId && (
        <div className="text-[10px] text-amber-300 inline-flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Awaiting confirmation
        </div>
      )}
      {needsAssignment && onAssign && (
        <button
          type="button"
          onClick={() => onAssign(ev)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#B0FC38]/[0.1] border border-[#B0FC38]/30 text-[10px] text-[#B0FC38] hover:bg-[#B0FC38]/[0.2]"
        >
          <UserPlus className="w-3 h-3" /> Assign
        </button>
      )}
      <div className="w-full mt-2 pt-2 border-t border-white/[0.04]">
        <EventOverrides event={ev} onChanged={onChanged} />
      </div>
    </div>
  );
}

function daysUntil(dateStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  if (Number.isNaN(d.valueOf())) return Infinity;
  return Math.ceil((d - new Date()) / 86_400_000);
}
